/**
 * Calendar API Client
 * Meeting Prep Feature - Kalenderintegration für Email-Board
 */

import api from './client';

// ============ Types ============

export interface CalendarAttendee {
  email: string;
  name?: string;
  status: 'accepted' | 'declined' | 'tentative' | 'needsAction' | 'none';
  isOrganizer?: boolean;
}

export interface CalendarEvent {
  id: string;
  mailAccountId: string;
  title: string;
  description?: string;
  location?: string;
  start: string; // ISO date string
  end: string;   // ISO date string
  isAllDay: boolean;
  attendees: CalendarAttendee[];
  organizer?: CalendarAttendee;
  meetingLink?: string; // Teams/Meet/Zoom link
  recurrenceId?: string;
  isRecurring: boolean;
  isCancelled: boolean;
  webLink?: string; // Link zum Event im Outlook/Google Calendar
  createdAt: string;
  updatedAt: string;
}

export interface RelatedEmail {
  messageId: string;
  mailAccountId: string;
  subject: string;
  from: {
    email: string;
    name?: string;
  };
  receivedAt: string;
  bodyPreview?: string;
  hasAttachment: boolean;
  isRead: boolean;
  matchType: 'attendee' | 'subject' | 'thread' | 'manual';
  matchConfidence: number; // 0-100
}

export interface MeetingPrep {
  event: CalendarEvent;
  relatedEmails: RelatedEmail[];
  notes?: string;
  lastPreparedAt?: string;
}

export interface EventWithEmails {
  event: CalendarEvent;
  emails: RelatedEmail[];
  emailCount: number;
}

// ============ API Client ============

export const calendarApi = {
  /**
   * Get upcoming calendar events
   * @param accountId - Mail account ID
   * @param days - Number of days to look ahead (default: 7)
   */
  getUpcomingEvents: (accountId: string, days: number = 7) =>
    api.get(`calendar/${accountId}/events`, {
      searchParams: { days },
    }).json<{ events: CalendarEvent[] }>(),

  /**
   * Get a single calendar event by ID
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   */
  getEvent: (accountId: string, eventId: string) =>
    api.get(`calendar/${accountId}/events/${eventId}`).json<{ event: CalendarEvent }>(),

  /**
   * Get emails related to a calendar event
   * Based on: attendees, subject keywords, thread context
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   */
  getRelatedEmails: (accountId: string, eventId: string) =>
    api.get(`calendar/${accountId}/events/${eventId}/related-emails`).json<{
      emails: RelatedEmail[];
      matchStats: {
        byAttendee: number;
        bySubject: number;
        byThread: number;
        byManual: number;
      };
    }>(),

  /**
   * Get all upcoming events with their related emails (combined view)
   * @param accountId - Mail account ID
   * @param days - Number of days to look ahead
   */
  getEventsWithEmails: (accountId: string, days: number = 7) =>
    api.get(`calendar/${accountId}/events-with-emails`, {
      searchParams: { days },
    }).json<{ events: EventWithEmails[] }>(),

  /**
   * Manually link an email to an event
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   * @param messageId - Email message ID to link
   */
  linkEmailToEvent: (accountId: string, eventId: string, messageId: string) =>
    api.post(`calendar/${accountId}/events/${eventId}/link-email`, {
      json: { messageId },
    }).json<{ message: string }>(),

  /**
   * Remove manual email-event link
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   * @param messageId - Email message ID to unlink
   */
  unlinkEmailFromEvent: (accountId: string, eventId: string, messageId: string) =>
    api.delete(`calendar/${accountId}/events/${eventId}/link-email`, {
      json: { messageId },
    }).json<{ message: string }>(),

  /**
   * Save meeting prep notes for an event
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   * @param notes - Prep notes text
   */
  savePrepNotes: (accountId: string, eventId: string, notes: string) =>
    api.put(`calendar/${accountId}/events/${eventId}/notes`, {
      json: { notes },
    }).json<{ message: string; savedAt: string }>(),

  /**
   * Get meeting prep notes for an event
   * @param accountId - Mail account ID
   * @param eventId - Calendar event ID
   */
  getPrepNotes: (accountId: string, eventId: string) =>
    api.get(`calendar/${accountId}/events/${eventId}/notes`).json<{
      notes: string | null;
      lastUpdated: string | null;
    }>(),

  /**
   * Sync calendar from provider (Microsoft/Google)
   * @param accountId - Mail account ID
   */
  syncCalendar: (accountId: string) =>
    api.post(`calendar/${accountId}/sync`).json<{
      synced: number;
      added: number;
      updated: number;
      deleted: number;
    }>(),
};

// ============ Helper Functions ============

/**
 * Calculate time until event starts
 * Returns human-readable countdown string
 */
export function getEventCountdown(eventStart: string): {
  text: string;
  urgency: 'now' | 'soon' | 'today' | 'tomorrow' | 'thisWeek' | 'later';
  minutesUntil: number;
} {
  const now = new Date();
  const start = new Date(eventStart);
  const diffMs = start.getTime() - now.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Already started or starting now
  if (diffMinutes <= 0) {
    return { text: 'Jetzt', urgency: 'now', minutesUntil: diffMinutes };
  }

  // Within next 30 minutes
  if (diffMinutes <= 30) {
    return { text: `In ${diffMinutes} Min.`, urgency: 'soon', minutesUntil: diffMinutes };
  }

  // Within next hour
  if (diffMinutes <= 60) {
    return { text: `In ${diffMinutes} Min.`, urgency: 'soon', minutesUntil: diffMinutes };
  }

  // Today
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  if (start <= todayEnd) {
    return {
      text: `Heute ${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
      urgency: 'today',
      minutesUntil: diffMinutes,
    };
  }

  // Tomorrow
  const tomorrowEnd = new Date(todayEnd);
  tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
  if (start <= tomorrowEnd) {
    return {
      text: `Morgen ${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
      urgency: 'tomorrow',
      minutesUntil: diffMinutes,
    };
  }

  // This week (within 7 days)
  if (diffDays < 7) {
    const weekday = start.toLocaleDateString('de-DE', { weekday: 'short' });
    const time = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return {
      text: `${weekday} ${time}`,
      urgency: 'thisWeek',
      minutesUntil: diffMinutes,
    };
  }

  // Later
  const dateStr = start.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  const time = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return {
    text: `${dateStr} ${time}`,
    urgency: 'later',
    minutesUntil: diffMinutes,
  };
}

/**
 * Format event duration
 */
export function formatEventDuration(start: string, end: string, isAllDay: boolean): string {
  if (isAllDay) return 'Ganztägig';

  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes} Min.`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (minutes === 0) {
    return `${hours} Std.`;
  }

  return `${hours} Std. ${minutes} Min.`;
}

/**
 * Group events by day for display
 */
export function groupEventsByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const grouped = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const dateKey = new Date(event.start).toISOString().split('T')[0];
    const existing = grouped.get(dateKey) || [];
    existing.push(event);
    grouped.set(dateKey, existing);
  }

  // Sort events within each day by start time
  for (const [key, dayEvents] of grouped) {
    grouped.set(
      key,
      dayEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    );
  }

  return grouped;
}

/**
 * Get initials from attendee name/email for avatar
 */
export function getAttendeeInitials(attendee: CalendarAttendee): string {
  if (attendee.name) {
    const parts = attendee.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return attendee.name.slice(0, 2).toUpperCase();
  }
  return attendee.email.slice(0, 2).toUpperCase();
}

/**
 * Check if event is happening soon (within threshold minutes)
 */
export function isEventSoon(eventStart: string, thresholdMinutes: number = 60): boolean {
  const now = new Date();
  const start = new Date(eventStart);
  const diffMs = start.getTime() - now.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  return diffMinutes > 0 && diffMinutes <= thresholdMinutes;
}
